import { Icon, Intent } from "@blueprintjs/core";
import {
  CollectionRole,
  DOCS_LINKS,
  GroupId,
  HexType,
  OrgRole,
  ProjectRole,
  UserId,
  guardNever,
  humanReadableOrgRole,
  humanReadableProjectRole,
  isOrgRoleSuperset,
} from "@hex/common";
import React, { ReactNode } from "react";
import styled from "styled-components";

import { HexCleanLink, HexTooltip, Txt } from "../../hex-components";
import { HexButton } from "../../hex-components/HexButton.js";
import { useCurrentUser } from "../../hooks/me/useCurrentUser.js";
import {
  PermissionListAppUserReasonFragment,
  PermissionListDenialReasonFragment,
  PermissionListViewerReasonFragment,
} from "../../mutations/permissions.generated";
import { Routes, WorkspaceSettingsTabs } from "../../route/routes.js";
import { useProjectContext } from "../../util/projectContext";
import { maybePluralize } from "../../util/string.js";
import { ContactAnAdmin } from "../common/ContactAnAdmin";
import { DocsLink } from "../common/DocsLink.js";
import { GroupsIcon, PublishIcon, WarningIcon } from "../icons/CustomIcons";
import { Avatar } from "../user/Avatar";
import { UserAvatar } from "../user/UserAvatar";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  transition: background-color
    ${({ theme }) => `${theme.animation.duration} ${theme.animation.easing}`};

  &:hover {
    background-color: ${({ theme }) => theme.hoverColor};
  }
`;

const UserErrorDiv = styled.div`
  flex: none;
  width: 28px;
`;

const UserDiv = styled.div`
  display: flex;
  flex-grow: 1;
  gap: 8px;
  align-items: center;
  min-width: 0;
  height: 30px;
  padding-right: 10px;
`;

const UserInfoDiv = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const UserRoleRowRightDiv = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const RoleDiv = styled.div`
  flex-grow: 1;
`;

const AssetList = styled.ul`
  margin: 8px 0;
  padding-left: 16px;

  li {
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

interface PermissionRowItemProps {
  minimal?: boolean;
  avatar: ReactNode;
  name: string;
  label: string;
  isOwner?: boolean;
}

export const PermissionRowItem: React.ComponentType<PermissionRowItemProps> =
  React.memo(function PermissionRowItem({
    avatar,
    isOwner,
    label,
    minimal = false,
    name,
  }) {
    return (
      <UserDiv>
        {avatar}
        <UserInfoDiv>
          <HexTooltip content={label} disabled={!label} position="bottom">
            <Txt ellipsize={true} fontSize={minimal ? "small" : undefined}>
              {name}
              {isOwner && (
                <>
                  {" "}
                  <Txt fontColor="muted">(Owner)</Txt>
                </>
              )}
            </Txt>
          </HexTooltip>
        </UserInfoDiv>
      </UserDiv>
    );
  });

export type PermissionRowData = PermissionsRowUserData | PermissionsGroupData;

export type BasePermissionRowData = {
  role: ProjectRole | CollectionRole;
};

export type PermissionsRowUserData = {
  dataType: "user";
  id: UserId;
  /**
   * If this user is the current Project Owner.
   * This is helpful to determine when or if downgrading roles is allowed for the user.
   */
  isOwner?: boolean;
  name?: string | null;
  email: string;
  active?: boolean;
  orgRole: OrgRole;
  imageUrl?: string;
  viewerReason?: PermissionListViewerReasonFragment | null;
  appUserReason?: PermissionListAppUserReasonFragment | null;
  denialReason?: PermissionListDenialReasonFragment | null;
} & BasePermissionRowData;

export type PermissionsGroupData = {
  dataType: "group";
  id: GroupId;
  name: string;
} & BasePermissionRowData;

export interface PermissionRowProps {
  data: PermissionRowData;
  isComponent?: boolean;
  rolePicker?: ReactNode;
  minimal?: boolean;
  className?: string;
  unpublishedChanges?: boolean;
}

export const PermissionRow: React.ComponentType<PermissionRowProps> =
  React.memo(function PermissionRow({
    className,
    data,
    isComponent = false,
    minimal = false,
    rolePicker,
    unpublishedChanges,
  }) {
    const maybeProjectContext = useProjectContext({ safe: true });
    const hexType = maybeProjectContext?.hexType;
    const currentUser = useCurrentUser();
    let warningMessage: JSX.Element | string | undefined = undefined;
    const entityType = hexType === HexType.ASK ? "thread" : "project";

    if (data.dataType === "user") {
      const userLabel = data.name ?? data.email;
      const isCurrentUserAdmin =
        currentUser != null &&
        isOrgRoleSuperset(currentUser?.orgRole, OrgRole.ADMIN);

      if (data.denialReason != null) {
        switch (data.denialReason.__typename) {
          case "AssetAccess":
            warningMessage = (
              <MissingAssetsTooltipContent
                action={`cannot access ${entityType}`}
                assetAccess={data.denialReason}
                isCurrentUserAdmin={isCurrentUserAdmin}
                userLabel={userLabel}
              />
            );
            break;
          case "MissingProjectRole": // this denial reason is not relevant for the permission list
            break;
          default:
            warningMessage = `${userLabel} cannot access ${entityType}.`;
            guardNever(
              data.denialReason,
              (data.denialReason as { __typename: string }).__typename,
            );
        }
      } else if (data.appUserReason != null) {
        const downgradedToLabel = (
          <>
            has been downgraded to{" "}
            <strong>
              {humanReadableProjectRole(ProjectRole.APP_USER, isComponent)}
            </strong>{" "}
            access
          </>
        );
        switch (data.appUserReason.__typename) {
          case "MissingOrgRole":
            warningMessage = generateMissingOrgRoleWarning({
              userLabel,
              isCurrentUserAdmin,
              downgradedToLabel,
              currentOrgRole: data.appUserReason.currentOrgRole,
              neededOrgRole: data.appUserReason.neededOrgRole,
            });
            break;
          case "MissingProjectRole": // this app user reason is not relevant for the permission list
            break;
          default:
            warningMessage = (
              <>
                {userLabel} {downgradedToLabel}.
              </>
            );
            guardNever(
              data.appUserReason,
              (data.appUserReason as { __typename: string }).__typename,
            );
        }
      } else if (
        data.role === ProjectRole.VIEWER &&
        data.viewerReason?.__typename === "AssetAccess"
      ) {
        // This logic check occurs when an OrgRole.MEMBER user is granted access to an Ask project - because OrgRole.MEMBER users cannot EFH, the typical logic will dwongrade them here
        // in asset access - however, we skip this if the hexType is an Ask and the user has OrgRole.MEMBER (not EXPLORER)
        // Notes - [1] we still properly warn if an Explorer+ is downgraded on the Ask project [2] we still properly warn if an OrgRole.MEMBER is removed entirely from the project due to missing asset access.
        const skipAssetDowngradeForAsk =
          hexType === HexType.ASK &&
          !isOrgRoleSuperset(data.orgRole, OrgRole.EXPLORER);
        if (!skipAssetDowngradeForAsk) {
          // User has can-explore access to the project, but the project uses an asset for which the user
          // has view-only access (no query/import permissions)
          warningMessage = (
            <MissingAssetsTooltipContent
              action="cannot explore from app"
              assetAccess={data.viewerReason}
              isCurrentUserAdmin={isCurrentUserAdmin}
              userLabel={userLabel}
            />
          );
        }
      } else if (data.viewerReason != null) {
        const downgradedToLabel = (
          <>
            has been downgraded to{" "}
            <strong>
              {humanReadableProjectRole(ProjectRole.VIEWER, isComponent)}
            </strong>{" "}
            access
          </>
        );
        switch (data.viewerReason.__typename) {
          case "AssetAccess":
            warningMessage = (
              <MissingAssetsTooltipContent
                action={downgradedToLabel}
                assetAccess={data.viewerReason}
                isCurrentUserAdmin={isCurrentUserAdmin}
                userLabel={userLabel}
              />
            );
            break;
          case "MissingOrgRole": {
            warningMessage = generateMissingOrgRoleWarning({
              userLabel,
              isCurrentUserAdmin,
              downgradedToLabel,
              currentOrgRole: data.viewerReason.currentOrgRole,
              neededOrgRole: data.viewerReason.neededOrgRole,
            });
            break;
          }
          case "OAuthCredsNotShared":
            warningMessage = (
              <>
                {userLabel} {downgradedToLabel} due to using an OAuth data
                connection without shared credentials.{" "}
                <DocsLink to={DOCS_LINKS.OAuthDataConnections}>
                  Learn more.
                </DocsLink>
              </>
            );
            break;
          case "OAuthCollaborationNonSessionOwner":
            break;
          case "MissingProjectRole": // this viewer reason is not relevant for the permission list
          case "NonDraftVersion": // we handle this case with a nav bar warning
          case "Archived": // we handle this case with a nav bar warning
          case "Trashed": // we handle this case with a nav bar warning
            break;
          default:
            warningMessage = (
              <>
                {userLabel} {downgradedToLabel}.
              </>
            );
            guardNever(
              data.viewerReason,
              (data.viewerReason as { __typename: string }).__typename,
            );
        }
      }
    }
    const unpublishedChangesWarning = (
      groupOrUser: "group" | "user",
    ): JSX.Element => {
      return (
        <>
          <strong>{`This ${entityType} has unpublished changes.`}</strong> This{" "}
          {groupOrUser} will only see the latest published version.
        </>
      );
    };
    const warningIcon =
      data.dataType === "user" && warningMessage != null ? (
        <WarningIcon intent={Intent.WARNING} />
      ) : (
        <PublishIcon />
      );

    if (unpublishedChanges && data.role === ProjectRole.APP_USER) {
      warningMessage = warningMessage
        ? warningMessage
        : unpublishedChangesWarning(data.dataType);
    }

    return (
      <Container className={className}>
        {data.dataType === "user" && data.active !== true && (
          <HexTooltip
            content="This user's account has been deactivated."
            placement="top"
          >
            <UserErrorDiv>
              <Icon icon="error" intent="warning" />
            </UserErrorDiv>
          </HexTooltip>
        )}
        {data.dataType === "user" ? (
          <PermissionRowItem
            avatar={
              <UserAvatar
                active={true}
                email={data.email}
                imageUrl={data.imageUrl ?? undefined}
                name={data.name ?? undefined}
                size={30}
              />
            }
            isOwner={data.isOwner}
            label={data.email}
            minimal={minimal}
            name={data.name || data.email}
          />
        ) : (
          <PermissionRowItem
            avatar={
              <Avatar
                active={true}
                altText={data.name}
                size={30}
                text={<GroupsIcon />}
              />
            }
            label=""
            minimal={minimal}
            name={data.name}
          />
        )}
        <UserRoleRowRightDiv>
          {warningMessage &&
          data.dataType === "user" &&
          data.denialReason?.__typename === "AssetAccess" ? (
            <MissingAssetsTooltip
              action={`cannot access ${entityType}`}
              assetAccess={data.denialReason}
              isCurrentUserAdmin={
                currentUser != null &&
                isOrgRoleSuperset(currentUser?.orgRole, OrgRole.ADMIN)
              }
              userLabel={data.name ?? data.email}
              variant="icon"
            />
          ) : warningMessage ? (
            <HexTooltip content={warningMessage} interactionKind="hover">
              {warningIcon}
            </HexTooltip>
          ) : null}
          <RoleDiv>{rolePicker}</RoleDiv>
        </UserRoleRowRightDiv>
      </Container>
    );
  });

function generateMissingOrgRoleWarning({
  currentOrgRole,
  downgradedToLabel,
  isCurrentUserAdmin,
  neededOrgRole,
  userLabel,
}: {
  downgradedToLabel: ReactNode;
  userLabel: ReactNode;
  neededOrgRole: OrgRole;
  currentOrgRole: OrgRole;
  isCurrentUserAdmin: boolean;
}): JSX.Element {
  const promotionPrompt = isCurrentUserAdmin ? (
    <HexCleanLink to={Routes.SETTINGS.getUrl({ subView: "users" })}>
      Upgrade
    </HexCleanLink>
  ) : (
    <>
      <ContactAnAdmin /> to upgrade
    </>
  );

  return (
    <>
      {userLabel} {downgradedToLabel} as they have the{" "}
      <strong>{humanReadableOrgRole(currentOrgRole)}</strong> role.
      <br /> {promotionPrompt} this user to an{" "}
      <strong>{humanReadableOrgRole(neededOrgRole)}</strong> role.
    </>
  );
}

export interface MissingAssetDetails {
  missingConnections: string[];
  missingPackages: string[];
  missingSecrets: string[];
  missingRepositories: string[];
  missingFiles: string[];
  numberOfAssetTypesMissingAccess: number;
  assetType: string;
  subView: WorkspaceSettingsTabs;
}

export function getMissingAssetDetails(assetAccess: {
  dataConnectionsMissingAccess: ReadonlyArray<{ connectionName: string }>;
  filesMissingAccess: ReadonlyArray<{ filename: string }>;
  hasAccessToSharedDataConnections: boolean;
  hasAccessToSharedFiles: boolean;
  hasAccessToSharedPackages: boolean;
  hasAccessToSharedSecrets: boolean;
  hasAccessToSharedSyncRepositories: boolean;
  secretsMissingAccess: ReadonlyArray<{ name: string }>;
  sharedPackagesMissingAccess: ReadonlyArray<{ repoName: string }>;
  syncRepositoriesMissingAccess: ReadonlyArray<{ repoName: string }>;
}): MissingAssetDetails {
  const missingConnections = assetAccess.dataConnectionsMissingAccess.map(
    (e) => e.connectionName,
  );

  const missingPackages = assetAccess.sharedPackagesMissingAccess.map(
    (e) => e.repoName,
  );
  const missingSecrets = assetAccess.secretsMissingAccess.map((e) => e.name);
  const missingRepositories = assetAccess.syncRepositoriesMissingAccess.map(
    (e) => e.repoName,
  );
  const missingFiles = assetAccess.filesMissingAccess.map((e) => e.filename);

  const numberOfAssetTypesMissingAccess = [
    assetAccess.hasAccessToSharedDataConnections,
    assetAccess.hasAccessToSharedPackages,
    assetAccess.hasAccessToSharedSecrets,
    assetAccess.hasAccessToSharedSyncRepositories,
    assetAccess.hasAccessToSharedFiles,
  ].reduce((acc, hasAccess) => (!hasAccess ? acc + 1 : acc), 0);

  let assetType: string;
  let subView: WorkspaceSettingsTabs;

  if (numberOfAssetTypesMissingAccess > 1) {
    assetType = "assets";
    subView = "general";
  } else if (!assetAccess.hasAccessToSharedDataConnections) {
    assetType =
      assetAccess.dataConnectionsMissingAccess.length > 1
        ? "data connections"
        : "data connection";
    subView = "data-sources";
  } else if (!assetAccess.hasAccessToSharedPackages) {
    assetType =
      assetAccess.sharedPackagesMissingAccess.length > 1
        ? "packages"
        : "package";
    subView = "integrations";
  } else if (!assetAccess.hasAccessToSharedSecrets) {
    assetType =
      assetAccess.secretsMissingAccess.length > 1 ? "secrets" : "secret";
    subView = "secrets";
  } else if (!assetAccess.hasAccessToSharedSyncRepositories) {
    assetType =
      assetAccess.syncRepositoriesMissingAccess.length > 1
        ? "sync repositories"
        : "sync repository";
    subView = "integrations";
  } else if (!assetAccess.hasAccessToSharedFiles) {
    assetType = assetAccess.filesMissingAccess.length > 1 ? "files" : "file";
    subView = "general";
  } else {
    assetType = "assets";
    subView = "general";
  }

  return {
    missingConnections,
    missingPackages,
    missingSecrets,
    missingRepositories,
    missingFiles,
    numberOfAssetTypesMissingAccess,
    assetType,
    subView,
  };
}

export interface MissingAssetsTooltipContentProps {
  assetAccess: {
    dataConnectionsMissingAccess: ReadonlyArray<{ connectionName: string }>;
    filesMissingAccess: ReadonlyArray<{ filename: string }>;
    hasAccessToSharedDataConnections: boolean;
    hasAccessToSharedFiles: boolean;
    hasAccessToSharedPackages: boolean;
    hasAccessToSharedSecrets: boolean;
    hasAccessToSharedSyncRepositories: boolean;
    secretsMissingAccess: ReadonlyArray<{ name: string }>;
    sharedPackagesMissingAccess: ReadonlyArray<{ repoName: string }>;
    syncRepositoriesMissingAccess: ReadonlyArray<{ repoName: string }>;
  };
  action?: ReactNode;
  isCurrentUserAdmin?: boolean;
  userLabel?: ReactNode;
}

export const MissingAssetsTooltipContent: React.ComponentType<MissingAssetsTooltipContentProps> =
  React.memo(function MissingAssetsTooltipContent({
    action = "have been downgraded",
    assetAccess,
    isCurrentUserAdmin = false,
    userLabel = "You",
  }) {
    const details = getMissingAssetDetails(assetAccess);

    const assetSections = [
      {
        key: "connections",
        items: details.missingConnections,
        singular: "Data connection",
        plural: "Data connections",
      },
      {
        key: "secrets",
        items: details.missingSecrets,
        singular: "Secret",
        plural: "Secrets",
      },
      {
        key: "files",
        items: details.missingFiles,
        singular: "File",
        plural: "Files",
      },
      {
        key: "packages",
        items: details.missingPackages,
        singular: "Git package",
        plural: "Git packages",
      },
      {
        key: "repositories",
        items: details.missingRepositories,
        singular: "Sync repository",
        plural: "Sync repositories",
      },
    ].filter((section) => section.items.length > 0);

    return (
      <>
        {userLabel} {action} due to missing permissions on shared workspace
        assets:
        <AssetList>
          {assetSections.map((section) => (
            <li key={section.key}>
              <strong>
                {maybePluralize(
                  section.items.length,
                  section.singular,
                  section.plural,
                )}
                :
              </strong>{" "}
              {section.items.join(", ")}
            </li>
          ))}
        </AssetList>
        {isCurrentUserAdmin ? (
          <>
            <HexCleanLink
              to={Routes.SETTINGS.getUrl({ subView: details.subView })}
            >
              Update shared asset permissions
            </HexCleanLink>
          </>
        ) : (
          <>
            <ContactAnAdmin /> to grant access.
          </>
        )}
      </>
    );
  });

export interface MissingAssetsTooltipProps {
  assetAccess: {
    dataConnectionsMissingAccess: ReadonlyArray<{ connectionName: string }>;
    filesMissingAccess: ReadonlyArray<{ filename: string }>;
    hasAccessToSharedDataConnections: boolean;
    hasAccessToSharedFiles: boolean;
    hasAccessToSharedPackages: boolean;
    hasAccessToSharedSecrets: boolean;
    hasAccessToSharedSyncRepositories: boolean;
    secretsMissingAccess: ReadonlyArray<{ name: string }>;
    sharedPackagesMissingAccess: ReadonlyArray<{ repoName: string }>;
    syncRepositoriesMissingAccess: ReadonlyArray<{ repoName: string }>;
  };
  action?: ReactNode;
  isCurrentUserAdmin?: boolean;
  userLabel?: ReactNode;
  // Trigger variants
  variant?: "icon" | "button";
  buttonText?: string;
  iconIntent?: Intent;
}

export const MissingAssetsTooltip: React.ComponentType<MissingAssetsTooltipProps> =
  React.memo(function MissingAssetsTooltip({
    action,
    assetAccess,
    buttonText = "Review & request access",
    iconIntent = Intent.WARNING,
    isCurrentUserAdmin,
    userLabel,
    variant = "icon",
  }) {
    const tooltipContent = (
      <MissingAssetsTooltipContent
        action={action}
        assetAccess={assetAccess}
        isCurrentUserAdmin={isCurrentUserAdmin}
        userLabel={userLabel}
      />
    );

    if (variant === "button") {
      return (
        <HexTooltip content={tooltipContent} interactionKind="hover">
          <HexButton intent={Intent.PRIMARY} small={true} text={buttonText} />
        </HexTooltip>
      );
    }

    // Default icon variant for share dialog
    return (
      <HexTooltip content={tooltipContent} interactionKind="hover">
        <WarningIcon intent={iconIntent} />
      </HexTooltip>
    );
  });
